import { useState, type KeyboardEvent } from 'react';
import {
  APP_HEADER_CLASS,
  APP_HEADER_INNER_CLASS,
  DatePickerSheet,
  ICON_BUTTON_CLASS,
} from '@mobile/ui';
import {
  cn,
  formatMonthDay,
  formatWeekday,
  haptic,
  shiftDateKey,
  todayKey,
} from '@shared/utils';
import { ChevronLeftIcon, ChevronRightIcon, SettingsIcon } from '@shared/ui';

export interface DateNavBarProps {
  dateKey: string;
  /** 有紀錄的日子，供日期選擇面板標記 */
  loggedDates: ReadonlySet<string>;
  onChange: (dateKey: string) => void;
  onOpenSettings: () => void;
}

/**
 * 單日檢視的日期切換列。固定在頂端，左右方向鍵亦可切換日期。
 * 右側另放設定入口——它是全 app 唯一常駐的橫幅，設定不屬於任何一天，放這裡才不會
 * 讓人以為是「這天的設定」。
 */
export function DateNavBar({
  dateKey,
  loggedDates,
  onChange,
  onOpenSettings,
}: DateNavBarProps) {
  const today = todayKey();
  const isToday = dateKey === today;

  const [isPickerOpen, setIsPickerOpen] = useState(false);

  /* 觸覺回饋只掛在指標操作上：鍵盤切換日期時使用者的手不在裝置上，震動只是噪音 */
  const selectDate = (nextDateKey: string) => {
    haptic('tap');
    onChange(nextDateKey);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'ArrowLeft') onChange(shiftDateKey(dateKey, -1));
    if (event.key === 'ArrowRight') onChange(shiftDateKey(dateKey, 1));
  };

  return (
    <>
      {/*
        橫幅的外觀（底色、模糊、下緣線、safe-area、對齊線）走 @mobile/ui 的共用約定，
        兩個 app 的頂端橫幅因此長得一樣高、一樣的邊界；這裡只決定裡面放什麼。
      */}
      <header className={APP_HEADER_CLASS}>
        <div className={APP_HEADER_INNER_CLASS}>
          <div
            /* 圖示按鈕的內距讓文字視覺上內縮，-ml 補回來對齊卡片左緣 */
            className="-ml-2.5 flex items-center gap-1"
            role="group"
            aria-label="切換日期"
            onKeyDown={handleKeyDown}
          >
            <button
              type="button"
              className={ICON_BUTTON_CLASS}
              onClick={() => selectDate(shiftDateKey(dateKey, -1))}
              aria-label="前一天"
            >
              <ChevronLeftIcon />
            </button>

            {/*
              日期本身即為選擇入口：左右箭頭走的是相鄰的日子，要跳到兩週前只能靠它。
              標題維持 h1（它仍是這個畫面的標題），互動語意由內層的 button 承載——
              把 h1 自己變成可點擊元素會讓螢幕閱讀器少掉一個標題地標。
            */}
            <h1 className="flex">
              <button
                type="button"
                onClick={() => {
                  haptic('tap');
                  setIsPickerOpen(true);
                }}
                aria-haspopup="dialog"
                aria-expanded={isPickerOpen}
                aria-label={`選擇日期，目前是 ${formatMonthDay(dateKey)}`}
                className={cn(
                  'flex min-h-11 flex-col justify-center rounded-md px-2 leading-tight',
                  'transition-all duration-200 ease-out',
                  'hover:bg-foreground/5 active:scale-[0.98]',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                )}
              >
                <span className="font-display text-base tracking-tight">
                  {formatMonthDay(dateKey)}
                </span>
                <span className="font-mono text-[0.625rem] tracking-wide text-muted-foreground">
                  {formatWeekday(dateKey)}
                </span>
              </button>
            </h1>

            <button
              type="button"
              className={ICON_BUTTON_CLASS}
              onClick={() => selectDate(shiftDateKey(dateKey, 1))}
              aria-label="後一天"
            >
              <ChevronRightIcon />
            </button>
          </div>

          {/* 圖示按鈕的內距同樣讓視覺內縮，-mr 補回來對齊卡片右緣 */}
          <div className="-mr-2.5 flex items-center gap-1">
            <button
              type="button"
              onClick={() => selectDate(today)}
              aria-current={isToday ? 'date' : undefined}
              className={cn(
                'rounded-full border px-3 py-1.5 font-mono text-[0.625rem] tracking-wide uppercase',
                'transition-all duration-200 ease-out active:scale-[0.98]',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                isToday
                  ? 'border-accent/30 bg-accent-muted text-accent'
                  : 'border-border text-muted-foreground hover:border-border-hover hover:text-foreground',
              )}
            >
              今天
            </button>

            {/*
              設定放在 header 而非底部分頁：這個 app 只有「記錄」一個主要目的地，
              為了偏好設定挖掉一整條 tab bar 並不划算。等到出現與記錄平行的第二個
              目的地（例如統計報表），再升級成 IonTabs 才有意義。
            */}
            <button
              type="button"
              onClick={() => {
                haptic('tap');
                onOpenSettings();
              }}
              className={ICON_BUTTON_CLASS}
              aria-label="設定"
            >
              <SettingsIcon size={18} />
            </button>
          </div>
        </div>
      </header>

      {/*
        面板放在 <header> 之外。header 有 `backdrop-blur-md`，而 `backdrop-filter`
        會為子孫的 `position: fixed` 建立包含區塊——IonModal 是 fixed 定位，放進去
        會被壓縮在日期列那一條的範圍內，只露出一小片。
      */}
      <DatePickerSheet
        isOpen={isPickerOpen}
        dateKey={dateKey}
        highlightedDates={loggedDates}
        onSelect={onChange}
        onClose={() => setIsPickerOpen(false)}
      />
    </>
  );
}
